import { Link } from "@tanstack/react-router";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import type { Locale } from "@/lib/i18n";
import type { HomeProject } from "./types";

type WorkCopy = { eyebrow: string; title: string; all: string; context?: string };

const LABELS = {
  en: {
    intervention: "Intervention",
    result: "Verifiable result",
    context: "We show what was done, what could be verified, and the context the evidence needs.",
    live: "Live store",
    verify: "Measured on the live storefront. You can open it and check it yourself.",
  },
  es: {
    intervention: "Intervención",
    result: "Resultado comprobable",
    context: "Mostramos lo que se hizo, qué se pudo comprobar y el contexto que necesita la evidencia.",
    live: "Tienda en vivo",
    verify: "Medido sobre la tienda en producción. Puedes abrirla y comprobarlo tú mismo.",
  },
} as const;

function ProjectCard({ project, locale, tone }: { project: HomeProject; locale: Locale; tone: "light" | "dark" }) {
  const l = LABELS[locale === "es" ? "es" : "en"];
  const tag = locale === "es" ? project.tag_es : project.tag_en;
  const body = locale === "es" ? project.body_es : project.body_en;

  return (
    <article className="home-work-card" data-tone={tone} data-surface={tone}>
      <div className="home-work-card__identity">
        <span className="home-work-card__ring" aria-hidden />
        <div className="relative">
          <p className="home-work-card__sector">{tag}</p>
          <h3 className="home-work-card__title">{project.title}</h3>
        </div>
        {project.external_url ? (
          <a
            className="home-work-card__live"
            href={project.external_url}
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            {l.live}
            <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
          </a>
        ) : null}
      </div>

      <div className="home-work-card__block">
        <p className="home-work-card__label">{l.intervention}</p>
        <p className="home-work-card__body">{body}</p>
      </div>

      <div className="home-work-card__block home-work-card__block--result">
        <p className="home-work-card__label">{l.result}</p>
        {project.metrics && project.metrics.length > 0 ? (
          <dl className="home-work-card__metrics">
            {project.metrics.slice(0, 3).map((metric) => (
              <div key={metric.label_en}>
                <dd className="home-work-card__metric-value">{metric.value}</dd>
                <dt className="home-work-card__metric-label">{locale === "es" ? metric.label_es : metric.label_en}</dt>
              </div>
            ))}
          </dl>
        ) : (
          <p className="home-work-card__body">{body}</p>
        )}
        {project.metrics && project.metrics.length > 0 ? (
          <p className="home-work-card__verify">{l.verify}</p>
        ) : null}
      </div>
    </article>
  );
}

export function WorkSection({
  locale,
  work,
  featured,
  others,
}: {
  locale: Locale;
  work: WorkCopy;
  featured: HomeProject | undefined;
  others: HomeProject[];
}) {
  const l = LABELS[locale === "es" ? "es" : "en"];
  const projects = [featured, ...others].filter(Boolean).slice(0, 3) as HomeProject[];

  return (
    <div className="mx-auto max-w-7xl px-6">
      <Reveal>
        <div className="grid gap-8 lg:grid-cols-12 lg:items-start">
          <div className="lg:col-span-7">
            <p className="home-work-eyebrow">
              <span className="home-work-eyebrow__dot" aria-hidden />
              {work.eyebrow}
            </p>
            <h2 className="mt-6 max-w-3xl font-display text-4xl font-semibold leading-[1.06] tracking-tight sm:text-5xl">
              {work.title}
            </h2>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <p className="home-work-context">{work.context ?? l.context}</p>
          </div>
        </div>
      </Reveal>

      <div className="mt-14 space-y-5">
        {projects.map((project, index) => (
          <Reveal key={project.id} delay={index * 70}>
            <ProjectCard project={project} locale={locale} tone={index === 0 ? "light" : "dark"} />
          </Reveal>
        ))}
      </div>

      <Reveal delay={80}>
        <Link
          to={locale === "es" ? "/es/proyectos" : "/work"}
          className="home-work-cta group mt-10 inline-flex items-center gap-2"
        >
          {work.all}
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
        </Link>
      </Reveal>
    </div>
  );
}
